import { ImageErrorIcon } from 'tdesign-icons-vue-next';
import { PropType, computed, defineComponent, onMounted, ref, toRefs, watch } from 'vue';
import { useConfig, usePrefixClass, useImagePreviewUrl } from '@tdesign/shared-hooks';

import { useDrag } from '../hooks';
import { TdImageViewerProps } from '../type';

export default defineComponent({
  name: 'TImageItem',
  props: {
    rotate: Number,
    scale: Number,
    mirror: Number,
    src: [String, Object] as PropType<string | File>,
    placementSrc: [String, Object] as PropType<string | File>,
    isSvg: Boolean,
    imageReferrerpolicy: String as PropType<TdImageViewerProps['imageReferrerpolicy']>,
  },

  setup(props, { expose }) {
    const { src, placementSrc, isSvg } = toRefs(props);
    const classPrefix = usePrefixClass();
    const error = ref(false);
    const loaded = ref(false);
    const { transform, mouseDownHandler, resetTransform } = useDrag({ translateX: 0, translateY: 0 });
    const { globalConfig } = useConfig('imageViewer');
    const errorText = globalConfig.value.errorText;
    const svgElRef = ref<HTMLDivElement>();
    const modalBoxRef = ref<HTMLDivElement>();

    // --- 向中心缩放的 CSS transition 动画 ---
    const transitioningClass = `${classPrefix.value}-image-viewer__modal-box--transitioning`;
    let transitionEndHandler: ((e: TransitionEvent) => void) | null = null;
    /** 兜底定时器：浏览器可能不触发 transitionend（DOM 移除、动画合并、起止值相同等） */
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    /** 移除 transition 类名并清理所有监听 */
    const cleanupTransition = () => {
      const modalBox = modalBoxRef.value;
      if (!modalBox) return;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      modalBox.classList.remove(transitioningClass);
      if (transitionEndHandler) {
        modalBox.removeEventListener('transitionend', transitionEndHandler);
        transitionEndHandler = null;
      }
    };

    /**
     * 启用 modal-box 的 transition 动画
     * 流程：清理旧状态 → 强制 reflow → 添加类名 → 设置兜底超时 + transitionend 监听
     */
    const enableTransition = () => {
      const modalBox = modalBoxRef.value;
      if (!modalBox) return;

      cleanupTransition();
      modalBox.getBoundingClientRect(); // 强制 reflow，保证移除→添加类名能触发新动画
      modalBox.classList.add(transitioningClass);

      // 兜底：350ms 后强制清理（transition-duration 一般 ≤ 300ms）
      fallbackTimer = setTimeout(cleanupTransition, 350);

      const handleTransitionEnd = (e: TransitionEvent) => {
        if (e.propertyName !== 'transform') return;
        cleanupTransition();
      };
      transitionEndHandler = handleTransitionEnd;
      modalBox.addEventListener('transitionend', handleTransitionEnd);
    };

    // 暴露内部状态，供父组件在缩放时读写 transform / 启用过渡动画
    expose({ modalBoxRef, transform, resetTransform, enableTransition });

    // 双层 transform 架构：
    // - modal-image（内层）：rotateZ + scale → CSS 自带 transition: all 自动驱动平滑动画
    // - modal-box（外层）：translate(拖拽位移) + scale(mirror, 1) → 位移和镜像，无 transition 保证拖拽跟手
    const imgStyle = computed(() => ({
      transform: `rotateZ(${props.rotate}deg) scale(${props.scale})`,
      display: !props.placementSrc || loaded.value ? 'block' : 'none',
    }));
    const placementImgStyle = computed(() => ({
      transform: `rotateZ(${props.rotate}deg) scale(${props.scale})`,
      display: !loaded.value ? 'block' : 'none',
    }));
    const boxStyle = computed(() => {
      const { translateX, translateY } = transform.value;
      return {
        transform: `translate(${translateX}px, ${translateY}px) scale(${props.mirror}, 1)`,
      };
    });

    const resetStatus = () => {
      error.value = false;
      loaded.value = false;
      if (isSvg.value) {
        createSvgShadow(mainImagePreviewUrl.value);
      }
    };

    const createSvgShadow = async (url: string) => {
      const response = await fetch(url);
      if (!response.ok) {
        error.value = true;
        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
      }
      const svgText = await response.text();

      const element = svgElRef.value;
      element.innerHTML = '';
      element.classList?.add(`${classPrefix.value}-image-viewer__modal-image-svg`);
      const shadowRoot = element.attachShadow({ mode: 'closed' });

      const container = document.createElement('div');

      container.style.background = 'var(--td-bg-color-container)';
      container.style.padding = '4px';
      container.style.borderRadius = '4px';
      container.style.maxHeight = '100%';
      container.style.maxWidth = '100%';
      container.style.boxSizing = 'border-box';
      container.style.height = 'auto';
      container.innerHTML = svgText;
      shadowRoot.appendChild(container);

      const svgElement = container.querySelector('svg');
      if (svgElement) {
        const svgViewBox = svgElement.getAttribute('viewBox');
        if (svgViewBox) {
          const viewBoxValues = svgViewBox
            .split(/[\s,]/)
            .filter(function (v) {
              return v;
            })
            .map(parseFloat);

          // svg viewbox x(0) and y(1) offset, width(2) and height(3),eg
          const svgViewBoxWidth = viewBoxValues[2];
          // const svgViewBoxHeight = viewBoxValues[3];
          container.style.width = `${svgViewBoxWidth}px`;
        } else {
          const bbox = svgElement.getBBox();
          const calculatedViewBox = `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`;
          svgElement.setAttribute('viewBox', calculatedViewBox);

          container.style.width = `${bbox.width}px`;
        }
        svgElement.style.maxHeight = '100%';
        svgElement.style.maxWidth = '100%';
        svgElement.style.height = 'auto';
        svgElement.style.display = 'block';
        svgElement.style.lineHeight = 'normal';
      }
      loaded.value = true;
    };

    const { previewUrl: mainImagePreviewUrl } = useImagePreviewUrl(src);
    const { previewUrl: placementImagePreviewUrl } = useImagePreviewUrl(placementSrc);

    watch([mainImagePreviewUrl, placementImagePreviewUrl], () => {
      resetStatus();
    });

    onMounted(async () => {
      if (isSvg.value) {
        await createSvgShadow(mainImagePreviewUrl.value);
      }
    });

    return () => (
      <div class={`${classPrefix.value}-image-viewer__modal-pic`}>
        <div ref={modalBoxRef} class={`${classPrefix.value}-image-viewer__modal-box`} style={boxStyle.value}>
          {error.value && (
            <div class={`${classPrefix.value}-image-viewer__img-error`}>
              {/* 脱离文档流 */}
              <div class={`${classPrefix.value}-image-viewer__img-error-content`}>
                <ImageErrorIcon size="4em" />
                <div class={`${classPrefix.value}-image-viewer__img-error-text`}>{errorText}</div>
              </div>
            </div>
          )}

          {!error.value && !!props.placementSrc && placementImagePreviewUrl.value && (
            <img
              class={`${classPrefix.value}-image-viewer__modal-image`}
              onMousedown={(event: MouseEvent) => {
                event.stopPropagation();
                mouseDownHandler(event);
              }}
              onTouchstart={(event: TouchEvent) => {
                event.stopPropagation();
                mouseDownHandler(event);
              }}
              src={placementImagePreviewUrl.value}
              style={placementImgStyle.value}
              referrerpolicy={props.imageReferrerpolicy}
              alt="image"
              draggable="false"
            />
          )}

          {!error.value && mainImagePreviewUrl.value && !isSvg.value && (
            <img
              class={`${classPrefix.value}-image-viewer__modal-image`}
              onMousedown={(event: MouseEvent) => {
                event.stopPropagation();
                mouseDownHandler(event);
              }}
              onTouchstart={(event: TouchEvent) => {
                event.stopPropagation();
                mouseDownHandler(event);
              }}
              src={mainImagePreviewUrl.value}
              onLoad={() => (loaded.value = true)}
              onError={() => (error.value = true)}
              style={imgStyle.value}
              referrerpolicy={props.imageReferrerpolicy}
              alt="image"
              draggable="false"
            />
          )}

          {!error.value && mainImagePreviewUrl.value && isSvg.value && (
            <div
              ref={svgElRef}
              class={`${classPrefix.value}-image-viewer__modal-image`}
              onMousedown={(event: MouseEvent) => {
                event.stopPropagation();
                mouseDownHandler(event);
              }}
              onTouchstart={(event: TouchEvent) => {
                event.stopPropagation();
                mouseDownHandler(event);
              }}
              data-alt="svg"
              style={imgStyle.value}
              draggable="false"
            ></div>
          )}
        </div>
      </div>
    );
  },
});
